import { isFoodCartVendorOrganization } from 'components/common/vendorConfig';
import FHIRFetcher from 'data/FHIRFetcher';
import { ACTIVITY_TAB } from 'fhir-sdoh/common/constants';
import * as fhirpath from 'fhirpath';
import { useAuthBroadcast } from 'hooks/use-auth-broadcast';
import {
  ACTIVITY_STORAGE_KEY,
  IDLE_STATE_KEY,
  useCustomIdleTimer,
} from 'hooks/use-custom-idle-timer';
import { isArray } from 'lodash';
import moment from 'moment-timezone';
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { completeSignInRedirect, getCurrentSession, refreshSession, signOut } from 'services/auth';
import type { ResourceEntry } from 'utils/bundleNarrowing';

import type { EligibilityGroup } from './AuthContext';

type Option = {
  key: string;
  text: string;
  value: string;
};

/**
 * A dropdown option built directly from a FHIR `Organization`.
 *
 * `Organization.id` and `.name` are both `0..1`, and the org loop below reads them
 * straight into the triple. Kept separate from `Option` — which the rest of `AppState`
 * uses and where the strings are genuine — so this optionality stays where it is true
 * rather than being pushed onto every consumer of the payer and vendor lists.
 */
type OrgOption = {
  key: string | undefined;
  text: string | undefined;
  value: string | undefined;
};

interface AuthState {
  loggedIn: boolean;
  token: string | null;
  tokenExpiration: number | null;
  userEmail: string | null;
}

interface AppState {
  eligibilityGroups?: EligibilityGroup[];
  /**
   * The option lists are always present: the initial `useState`, the logout reset and
   * the post-login fetch all seed arrays (empty when there is nothing to show), so
   * consumers may read them without a presence check.
   */
  payers: Option[];
  practitionerId: string | null;
  practitionerName?: string | null;
  validateUSPSAddress?: boolean | null;
  vendors: Option[];
  foodCardVendors: Option[];
  gender?: string | null;
}

interface AppContextType {
  authState: AuthState;
  appState: AppState;
  loading: boolean;
  isLoggingOut: boolean;
  setAuthState: (state: AuthState) => void;
  login: (accessToken: string, tokenExpiration: number, email: string) => void;
  logout: () => void;
  showTokenExpiredPopup: boolean;
  showRefreshTokenExpiredPopup: boolean;
  setShowTokenExpiredPopup: (show: boolean) => void;
  setShowRefreshTokenExpiredPopup: (show: boolean) => void;
  handleTokenRefresh: () => Promise<void>;
  ensureValidToken: () => Promise<string | null>;
}

interface AppProviderProps {
  children: React.ReactNode;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const IDLE_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const TOKEN_CHECK_INTERVAL = 30000; // Backup heartbeat check every 30 seconds
const DAILY_SESSION_RESET_CHECK_INTERVAL = 60000; // Check for daily reset every minute

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const storedState = JSON.parse(localStorage.getItem('authState') || '{}') as Partial<AuthState>;
  const appStoredState = JSON.parse(localStorage.getItem('appState') || '{}') as Partial<AppState>;

  const [loading, setLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showTokenExpiredPopup, setShowTokenExpiredPopup] = useState(false);
  const [showRefreshTokenExpiredPopup, setShowRefreshTokenExpiredPopup] = useState(false);

  const [authState, setAuthState] = useState<AuthState>({
    loggedIn: storedState.loggedIn || false,
    token: storedState.token || null,
    tokenExpiration: storedState.tokenExpiration || null,
    userEmail: storedState.userEmail || null,
  });

  const [appState, setAppState] = useState<AppState>(
    authState.loggedIn
      ? (appStoredState as AppState)
      : {
          eligibilityGroups: [],
          payers: [],
          practitionerId: null,
          practitionerName: null,
          validateUSPSAddress: null,
          vendors: [],
          foodCardVendors: [],
          gender: null,
        },
  );

  const tokenExpiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const dailyResetCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const loginDateRef = useRef<string | null>(null);
  const appDataFetchedRef = useRef(false);
  const isFetchingAppDataRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const isLoggingOutRef = useRef(false);
  const mountedRef = useRef(true);

  // auth cross-tab sync
  const {
    tabId,
    isLeader,
    authMessages,
    broadcastTokenExpired,
    broadcastTokenRefreshed,
    broadcastUserLoggedOut,
    broadcastUserLoggedIn,
    broadcastRefreshTokenExpired,
    broadcastHideTokenPopup,
  } = useAuthBroadcast();

  // Clear all timers and intervals
  const clearAllTimers = useCallback(() => {
    if (tokenExpiryTimeoutRef.current) {
      clearTimeout(tokenExpiryTimeoutRef.current);
      tokenExpiryTimeoutRef.current = null;
    }
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
      tokenCheckIntervalRef.current = null;
    }
    if (dailyResetCheckIntervalRef.current) {
      clearInterval(dailyResetCheckIntervalRef.current);
      dailyResetCheckIntervalRef.current = null;
    }
  }, []);

  const performPostSignoutCleanup = useCallback(() => {
    clearAllTimers();

    appDataFetchedRef.current = false;
    isFetchingAppDataRef.current = false;
    isRefreshingRef.current = false;
    loginDateRef.current = null;

    const newAuthState = {
      loggedIn: false,
      token: null,
      tokenExpiration: null,
      userEmail: null,
    };

    const newAppState = {
      eligibilityGroups: [],
      payers: [],
      practitionerId: null,
      practitionerName: null,
      validateUSPSAddress: null,
      vendors: [],
      foodCardVendors: [],
      gender: null,
    };

    setAuthState(newAuthState);
    setAppState(newAppState);
    setIsLoggingOut(false);
    setShowTokenExpiredPopup(false);
    setShowRefreshTokenExpiredPopup(false);

    localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    localStorage.removeItem(IDLE_STATE_KEY);
    localStorage.removeItem('authState');
    localStorage.removeItem('appState');
    localStorage.removeItem('loginDate');

    const hostName = window.location.hostname;
    localStorage.removeItem(`${hostName}-authToken`);
    localStorage.removeItem(`${hostName}-userEmail`);
    localStorage.removeItem(`${hostName}-practitionerId`);
    localStorage.removeItem(`${hostName}-practitionerName`);
    localStorage.removeItem(`${hostName}-gender`);
    localStorage.removeItem(`${hostName}-payers`);
    localStorage.removeItem(`${hostName}-vendors`);

    Object.values(ACTIVITY_TAB).forEach((tab) => sessionStorage.removeItem(`fetchedFilter:${tab}`));
  }, [clearAllTimers]);

  useEffect(() => {
    // process the latest message
    if (authMessages.length > 0) {
      const message = authMessages[authMessages.length - 1];

      // Unreachable: the enclosing `length > 0` already guarantees a last
      // element. Stated explicitly so the compiler can see what the guard above
      // already established — `noUncheckedIndexedAccess` types the index read as
      // optional regardless. The effect body's existing early return makes this
      // exactly equivalent to falling through.
      if (!message) {
        return;
      }

      // Don't process messages from the same tab
      if (message.payload?.tabId === tabId) {
        return;
      }

      switch (message.type) {
        case 'TOKEN_EXPIRED':
          if (!isLoggingOutRef.current) {
            setShowTokenExpiredPopup(true);
            setShowRefreshTokenExpiredPopup(false);
          }
          break;

        case 'TOKEN_REFRESHED':
          if (message.payload && !isLoggingOutRef.current) {
            const { token, tokenExpiration, userEmail } = message.payload;
            if (token && tokenExpiration && userEmail) {
              const newAuthState = {
                loggedIn: true,
                token,
                tokenExpiration,
                userEmail,
              };
              setAuthState(newAuthState);
              setShowTokenExpiredPopup(false);
              setShowRefreshTokenExpiredPopup(false);
            }
          }
          break;

        case 'USER_LOGGED_OUT':
          if (!isLoggingOutRef.current) {
            performPostSignoutCleanup();
          }
          break;

        case 'USER_LOGGED_IN':
          if (message.payload && !isLoggingOutRef.current) {
            const { token, tokenExpiration, userEmail } = message.payload;
            if (token && tokenExpiration && userEmail) {
              const newAuthState = {
                loggedIn: true,
                token,
                tokenExpiration,
                userEmail,
              };
              setAuthState(newAuthState);
              setShowTokenExpiredPopup(false);
              setShowRefreshTokenExpiredPopup(false);
              appDataFetchedRef.current = false; // Reset app data fetch flag
            }
          }
          break;

        case 'REFRESH_TOKEN_EXPIRED':
          if (!isLoggingOutRef.current) {
            setShowRefreshTokenExpiredPopup(true);
            setShowTokenExpiredPopup(false);
          }
          break;

        case 'HIDE_TOKEN_POPUP':
          setShowTokenExpiredPopup(false);
          setShowRefreshTokenExpiredPopup(false);
          break;
      }
    }
  }, [authMessages, tabId, performPostSignoutCleanup]);

  const isTokenExpired = useCallback((tokenExpiration?: number | null) => {
    if (!tokenExpiration) return true;
    return Date.now() >= tokenExpiration;
  }, []);

  const getTodayDateString = useCallback(() => {
    return moment().format('YYYY-MM-DD');
  }, []);

  const shouldCheckDailyReset = useCallback(() => {
    const now = moment();
    const hour = now.hour();

    // 3-hour grace period after midnight
    return hour >= 3;
  }, []);

  const shouldResetDailySession = useCallback(() => {
    const today = getTodayDateString();
    const storedLoginDate = localStorage.getItem('loginDate');

    if (!storedLoginDate || storedLoginDate !== today) {
      return true;
    }

    return false;
  }, [getTodayDateString]);

  // Logout function to handle user sign out
  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) {
      return;
    }

    // for internal checings of logging out state
    isLoggingOutRef.current = true;
    // for loading ui - logout button
    setIsLoggingOut(true);

    try {
      setShowTokenExpiredPopup(false);
      setShowRefreshTokenExpiredPopup(false);
      setLoading(false);

      // broadcast logout to other tabs
      broadcastUserLoggedOut();

      // Before the await, not after: `signoutRedirect()` never settles in the document it
      // navigates away from (oidc-client-ts 3.5.0 resolves on `pageshow`).
      performPostSignoutCleanup();

      await signOut();
    } catch (cause) {
      console.error(
        'care-betty: sign-out could not reach the provider; local session cleared',
        cause,
      );
    } finally {
      // `isLoggingOutRef` deliberately stays true: a logout has begun and must not be undone by
      // this document. Clearing it re-opens ~40 guards — including the effect that persists
      // `authState` — while the page is still rendered. It resets on the next mount.
      performPostSignoutCleanup();
      setIsLoggingOut(false);
    }
  }, [performPostSignoutCleanup, broadcastUserLoggedOut]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Callback to handle user inactivity
  const onIdle = useCallback(() => {
    if (showTokenExpiredPopup && authState.loggedIn && !isLoggingOutRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget sign-out; the local session is already cleared and a rejection changes nothing on screen
      logout();
    }
  }, [showTokenExpiredPopup, authState.loggedIn, logout]);

  // custom idle timer hook to handle user inactivity
  useCustomIdleTimer(IDLE_TIMEOUT, onIdle, showTokenExpiredPopup && authState.loggedIn);

  // Login function to set user authentication state
  const login = useCallback(
    (accessToken: string, tokenExpiration: number, email: string) => {
      if (isLoggingOutRef.current) {
        return;
      }

      appDataFetchedRef.current = false;

      const today = getTodayDateString();
      loginDateRef.current = today;
      localStorage.setItem('loginDate', today);

      const newAuthState = {
        loggedIn: true,
        token: accessToken,
        tokenExpiration,
        userEmail: email,
      };

      setAuthState(newAuthState);
      setShowTokenExpiredPopup(false);
      setShowRefreshTokenExpiredPopup(false);

      broadcastUserLoggedIn(accessToken, tokenExpiration, email);
    },
    [getTodayDateString, broadcastUserLoggedIn],
  );

  // only leader tab should manage token expiry timers
  // this is to setup daily session reset acting for rotating refresh token,
  // making sure that refresh token is invalidated at the end of the day
  const setupDailySessionReset = useCallback(() => {
    if (!isLeader) return;

    if (dailyResetCheckIntervalRef.current) {
      clearInterval(dailyResetCheckIntervalRef.current);
    }

    // NOTE: this is a check on mount if it's the right time
    if (
      authState.loggedIn &&
      shouldCheckDailyReset() &&
      shouldResetDailySession() &&
      !isLoggingOutRef.current
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget sign-out; the local session is already cleared and a rejection changes nothing on screen
      logout();
    }

    dailyResetCheckIntervalRef.current = setInterval(() => {
      if (
        authState.loggedIn &&
        shouldCheckDailyReset() &&
        shouldResetDailySession() &&
        !isLoggingOutRef.current
      ) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget sign-out; the local session is already cleared and a rejection changes nothing on screen
        logout();
      }
    }, DAILY_SESSION_RESET_CHECK_INTERVAL);
  }, [authState.loggedIn, shouldCheckDailyReset, shouldResetDailySession, logout, isLeader]);

  const setupTokenExpiryTimeout = useCallback(
    (tokenExpiration: number) => {
      // only leader tab should handle token expiry
      if (!isLeader) return;

      if (tokenExpiryTimeoutRef.current) {
        clearTimeout(tokenExpiryTimeoutRef.current);
      }

      const timeUntilExpiry = tokenExpiration - Date.now();

      if (timeUntilExpiry <= 0) {
        if (!isLoggingOutRef.current) {
          setShowTokenExpiredPopup(true);
          broadcastTokenExpired();
        }
        return;
      }

      tokenExpiryTimeoutRef.current = setTimeout(() => {
        if (!isLoggingOutRef.current) {
          setShowTokenExpiredPopup(true);
          broadcastTokenExpired();
        }
      }, timeUntilExpiry);
    },
    [isLeader, broadcastTokenExpired],
  );

  // this is setup for a backup heartbeat check to ensure token validity
  const setupTokenHeartbeat = useCallback(() => {
    // only leader tab should handle heartbeat
    if (!isLeader) return;

    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }

    tokenCheckIntervalRef.current = setInterval(() => {
      if (
        authState.loggedIn &&
        isTokenExpired(authState.tokenExpiration) &&
        !isLoggingOutRef.current
      ) {
        setShowTokenExpiredPopup(true);
        broadcastTokenExpired();
      }
    }, TOKEN_CHECK_INTERVAL);
  }, [
    authState.loggedIn,
    authState.tokenExpiration,
    isTokenExpired,
    isLeader,
    broadcastTokenExpired,
  ]);

  // Handle token refresh logic
  const handleTokenRefresh = useCallback(async () => {
    if (isRefreshingRef.current || isLoggingOutRef.current) return;

    try {
      isRefreshingRef.current = true;

      // MUST REFRESH, not read. This function exists solely to obtain a NEW token:
      // the user clicked "stay signed in" on the expiry popup. A plain read would
      // return the same expired token it already has and report success, leaving
      // the popup dismissed and every subsequent request 401ing.
      const {
        accessToken: refreshedAccessToken,
        email,
        expiresAtMs: tokenExpiration,
      } = await refreshSession();

      // Check if we're still mounted and not logging out
      if (!mountedRef.current || isLoggingOutRef.current) {
        return;
      }

      const newAuthState = {
        loggedIn: true,
        token: refreshedAccessToken,
        tokenExpiration,
        userEmail: email,
      };

      setAuthState(newAuthState);
      setShowTokenExpiredPopup(false);
      setShowRefreshTokenExpiredPopup(false);

      // broadcast token refresh to other tabs
      broadcastTokenRefreshed(refreshedAccessToken, tokenExpiration, email);
    } catch (error) {
      console.error('Token refresh failed:', error);
      if (!isLoggingOutRef.current) {
        setShowRefreshTokenExpiredPopup(true);
        setShowTokenExpiredPopup(false);
        broadcastRefreshTokenExpired();
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [broadcastTokenRefreshed, broadcastRefreshTokenExpired]);

  // Ensure valid token before making API calls
  const ensureValidToken = useCallback(
    // eslint-disable-next-line @typescript-eslint/require-await
    async (): Promise<string | null> => {
      if (isLoggingOutRef.current) {
        return null;
      }

      if (!authState.token || !authState.tokenExpiration) {
        return null;
      }

      if (isTokenExpired(authState.tokenExpiration)) {
        if (!isLoggingOutRef.current) {
          setShowTokenExpiredPopup(true);
          // Only leader tab should broadcast
          if (isLeader) {
            broadcastTokenExpired();
          }
        }
        return null;
      }

      return authState.token;
    },
    [authState.token, authState.tokenExpiration, isTokenExpired, isLeader, broadcastTokenExpired],
  );

  // custom setter for token expired popup that broadcasts to other tabs
  const setShowTokenExpiredPopupWithBroadcast = useCallback(
    (show: boolean) => {
      setShowTokenExpiredPopup(show);
      if (!show) {
        broadcastHideTokenPopup();
      }
    },
    [broadcastHideTokenPopup],
  );

  const setShowRefreshTokenExpiredPopupWithBroadcast = useCallback(
    (show: boolean) => {
      setShowRefreshTokenExpiredPopup(show);
      if (!show) {
        broadcastHideTokenPopup();
      }
    },
    [broadcastHideTokenPopup],
  );

  useEffect(() => {
    if (!authState.loggedIn || !authState.tokenExpiration || isLoggingOutRef.current) {
      clearAllTimers();
      return;
    }

    // setup timers (only master tab will actually create them)
    setupTokenExpiryTimeout(authState.tokenExpiration);
    setupTokenHeartbeat();
    setupDailySessionReset();

    return () => {
      clearAllTimers();
    };
  }, [
    authState.loggedIn,
    authState.tokenExpiration,
    setupTokenExpiryTimeout,
    setupTokenHeartbeat,
    setupDailySessionReset,
    clearAllTimers,
  ]);

  useEffect(() => {
    if (!isLoggingOutRef.current) {
      localStorage.setItem('authState', JSON.stringify(authState));
    }
  }, [authState]);

  useEffect(() => {
    if (authState.loggedIn && !isLoggingOutRef.current) {
      localStorage.setItem('appState', JSON.stringify(appState));
    }
  }, [appState, authState.loggedIn]);

  useEffect(() => {
    let mounted = true;

    const checkAuthStatus = async () => {
      if (isLoggingOutRef.current) {
        return;
      }

      try {
        // The code grant returns `?code=&state=`, which must be exchanged at the token
        // endpoint. Nothing else in the app performs that exchange, so without this
        // call sign-in never completes. Returns null on an ordinary page load, so the
        // stored-session path below runs instead.
        const callbackSession = await completeSignInRedirect();

        if (isLoggingOutRef.current || !mounted) {
          return;
        }

        if (callbackSession) {
          login(callbackSession.accessToken, callbackSession.expiresAtMs, callbackSession.email);
          return;
        }

        const session = await getCurrentSession();

        if (isLoggingOutRef.current || !mounted) {
          return;
        }

        // No stored session is the ordinary signed-out state, not an error:
        // `getCurrentSession()` returns null rather than rejecting, so the
        // signed-out reset is reached here rather than in the `catch` below.
        if (!session) {
          setAuthState({ loggedIn: false, token: null, tokenExpiration: null, userEmail: null });
          return;
        }

        if (mounted && !isLoggingOutRef.current) {
          if (isTokenExpired(session.expiresAtMs)) {
            try {
              // MUST REFRESH, not read. The guard above has already established the
              // stored token is expired, so a non-refreshing read would return that
              // same expired token and log the user in with it. This is what keeps a
              // returning user with a stale access token but a live refresh token
              // signed in.
              const refreshed = await refreshSession();

              if (isLoggingOutRef.current || !mounted) {
                return;
              }

              if (!isLoggingOutRef.current && mounted) {
                login(refreshed.accessToken, refreshed.expiresAtMs, refreshed.email);
              }
            } catch {
              if (!isLoggingOutRef.current && mounted) {
                setShowRefreshTokenExpiredPopupWithBroadcast(true);
              }
            }
          } else {
            login(session.accessToken, session.expiresAtMs, session.email);
          }
        }
      } catch (error) {
        // BOUND AND LOGGED, because the signed-out state this sets is indistinguishable from
        // "not signed in yet". A failure anywhere above — the code exchange, a missing claim, a
        // rejected scope — lands here and renders the login screen with no trace, which made a
        // real sign-in failure take four rounds of elimination to narrow. The user still sees the
        // login screen; the difference is that the reason is now readable.
        console.error('care-betty: auth bootstrap failed, treating as signed out', error);
        if (mounted && !isLoggingOutRef.current) {
          const newAuthState = {
            loggedIn: false,
            token: null,
            tokenExpiration: null,
            userEmail: null,
          };
          setAuthState(newAuthState);
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget; the effect cannot await, and a rejection leaves the existing auth state in place
    checkAuthStatus();

    return () => {
      mounted = false;
    };
  }, [login, isTokenExpired, setShowRefreshTokenExpiredPopupWithBroadcast]);

  useEffect(() => {
    const fetchAppData = async () => {
      if (isFetchingAppDataRef.current || appDataFetchedRef.current || isLoggingOutRef.current) {
        return;
      }

      const { token, userEmail, loggedIn } = authState;

      if (!loggedIn || !token || !userEmail) {
        return;
      }

      isFetchingAppDataRef.current = true;
      setLoading(true);

      try {
        const validToken = await ensureValidToken();
        if (!validToken || isLoggingOutRef.current) {
          throw new Error('No valid token available');
        }

        const practitioner = await FHIRFetcher(
          '/Practitioner?active=true&identifier=' + encodeURIComponent(userEmail),
          validToken,
        );

        if (isLoggingOutRef.current || !mountedRef.current) {
          return;
        }

        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. Four unguarded levels on the
         * login path: the Bundle itself, its `entry` (`0..*`, and absent entirely
         * on a search that matched nothing), the `[0]` element, and that entry's
         * `resource`.
         *
         * A signed-in user with no matching Practitioner resource — a new hire
         * whose FHIR record has not been created yet, or an identifier mismatch —
         * throws here, inside the async bootstrap, so the app renders its shell
         * with no session identity rather than reporting that the account is not
         * provisioned. That is the failure this app is most likely to hit in the
         * field, and the least legible when it does.
         */
        const practResource = practitioner!.entry![0]!.resource! as fhir4.Practitioner;
        const practId = practResource.id;
        const practName =
          (fhirpath.evaluate(practResource, 'name.given.first()') as unknown as string) +
          ' ' +
          (fhirpath.evaluate(practResource, 'name.family') as unknown as string);
        const practitionerGender = practResource.gender;

        const orgs = await FHIRFetcher<fhir4.Bundle>(
          '/Organization?active=true&_count=500',
          validToken,
        );

        if (isLoggingOutRef.current || !mountedRef.current) {
          return;
        }

        // SUSPECTED DEFECT — `orgs` is `Bundle | undefined` (`FHIRFetcher`
        // returns `undefined` for an empty endpoint) and `Bundle.entry` is
        // `0..*`, so both reads can be absent. The `for...of` below then throws
        // "undefined is not iterable" during app bootstrap, leaving the org /
        // vendor dropdowns unpopulated for the whole session. Asserted rather
        // than defaulted to `[]`: silently continuing would render an empty
        // program and vendor list, which looks like "this payer has no
        // programs" rather than a failed load.
        const orgsResource = orgs!.entry!;

        /*
         * `OrgOption` rather than `Option`: `Organization.id` and `.name` are both
         * `0..1` in FHIR, and this builder reads them straight into the triple.
         *
         * SUSPECTED DEFECT (one nameless organization breaks the whole session's
         * payer and vendor lists) — REPORTED, NOT FIXED. These three arrays are sorted
         * with `a.text.localeCompare(b.text)` where they are stored into `AppState`
         * below. `text` is `Organization.name`, which is `0..1`, so a single
         * organization without a name makes that comparator throw
         * `TypeError: Cannot read properties of undefined (reading 'localeCompare')`
         * inside the post-login fetch — which the surrounding comment already notes
         * leaves "the program and vendor dropdowns unpopulated for the whole session".
         *
         * The `Option` annotation that was here claimed `text: string` and hid this.
         * Left unfixed: sorting nameless orgs to either end, or dropping them, changes
         * which organizations a care manager can pick.
         */
        const programs: OrgOption[] = [];
        const foodVendors: OrgOption[] = [];
        const foodCardVendors: OrgOption[] = [];
        /*
         * Writes `eligibilityGroups`; `EligibilityGroup` in `contexts/AuthContext.tsx`
         * declares `eligiblityGroups`. Renaming either side makes
         * `OrganizationContext`'s dead branch start firing. See KNOWN_BUGS.md SH-2.
         */
        const eligibilityTags: { orgId: string | undefined; eligibilityGroups: string[] }[] = [];

        // Cast, not a runtime filter. Upstream iterates `orgs.entry` unfiltered
        // (`develop:src/contexts/AppContext.tsx:719` @ `f46fc9519`, a file with no filter call in
        // it). `isResourceEntry` is an `Array.prototype.filter` predicate, so narrowing with it
        // DROPS entries whose `resource` is absent (`0..1` in FHIR R4) or of another type — a
        // search bundle routinely carries `OperationOutcome` entries — and shortens the payer,
        // vendor and eligibility-tag lists below. The cast emits nothing.
        //
        // The reads below stay safe on a resource-less entry: all three branches read `orgType`
        // through `?.`, and `isFoodCartVendorOrganization(undefined)` returns `false`.
        for (const org of orgsResource as ResourceEntry<'Organization'>[]) {
          const orgResource = org.resource;
          const orgType = orgResource?.type?.[0]?.coding?.[0]?.code;
          const isFoodCartVendor = isFoodCartVendorOrganization(orgResource);

          if (orgType === 'pay') {
            programs.push({
              key: orgResource.id,
              value: orgResource.id,
              text: orgResource.name,
            });

            const eligibilityGroups = fhirpath.evaluate(
              org,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/eligibility-group').value",
            ) as string[];

            if (isArray(eligibilityGroups) && eligibilityGroups.length > 0) {
              eligibilityTags.push({
                orgId: orgResource.id,
                eligibilityGroups: eligibilityGroups,
              });
            }
          } else if (orgType === 'prov') {
            const vendorOption = {
              key: orgResource.id,
              value: orgResource.id,
              text: orgResource.name,
            };

            if (isFoodCartVendor) {
              foodCardVendors.push(vendorOption);
            } else {
              foodVendors.push(vendorOption);
            }
          } else if (isFoodCartVendor) {
            foodCardVendors.push({
              key: orgResource.id,
              value: orgResource.id,
              text: orgResource.name,
            });
          }
        }

        // final check before setting state
        if (isLoggingOutRef.current || !mountedRef.current) {
          return;
        }

        const fetchedData = {
          practitionerId: practId,
          practitionerName: practName,
          gender: practitionerGender,
          eligibilityGroups: eligibilityTags,
          // The `!`s keep the existing throw for a nameless organization rather than
          // introducing an ordering for one — see the defect note on `OrgOption` above.
          payers: programs.sort((a, b) => a.text!.localeCompare(b.text!)),
          vendors: foodVendors.sort((a, b) => a.text!.localeCompare(b.text!)),
          foodCardVendors: foodCardVendors.sort((a, b) => a.text!.localeCompare(b.text!)),
        };

        const hostName = window.location.hostname;
        localStorage.setItem(`${hostName}-authToken`, validToken);
        localStorage.setItem(`${hostName}-userEmail`, userEmail);
        // Both are `0..1` in FHIR; `setItem` stringifies, so a practitioner missing
        // either stores the literal string "undefined".
        localStorage.setItem(`${hostName}-practitionerId`, practId!);
        localStorage.setItem(`${hostName}-practitionerName`, practName);
        localStorage.setItem(`${hostName}-gender`, practitionerGender!);
        localStorage.setItem(`${hostName}-payers`, JSON.stringify(fetchedData.payers));
        localStorage.setItem(`${hostName}-vendors`, JSON.stringify(fetchedData.vendors));

        // Bridges the `eligibilityGroups`/`eligiblityGroups` spelling split; KNOWN_BUGS.md SH-2.
        setAppState(fetchedData as unknown as AppState);
        appDataFetchedRef.current = true;
      } catch (error) {
        console.error('Error fetching app data:', error);
        if (!isLoggingOutRef.current) {
          setShowTokenExpiredPopupWithBroadcast(true);
        }
      } finally {
        if (!isLoggingOutRef.current) {
          setLoading(false);
        }
        isFetchingAppDataRef.current = false;
      }
    };

    if (
      authState.loggedIn &&
      authState.token &&
      authState.userEmail &&
      !appDataFetchedRef.current &&
      !isLoggingOutRef.current
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget; the effect cannot await, and a rejection leaves the existing app data in place
      fetchAppData();
    }
  }, [
    authState.loggedIn,
    authState.token,
    authState.userEmail,
    ensureValidToken,
    setShowTokenExpiredPopupWithBroadcast,
  ]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return (
    <AppContext.Provider
      value={{
        authState,
        appState,
        setAuthState,
        login,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        logout,
        loading,
        isLoggingOut,
        showTokenExpiredPopup,
        showRefreshTokenExpiredPopup,
        setShowTokenExpiredPopup: setShowTokenExpiredPopupWithBroadcast,
        setShowRefreshTokenExpiredPopup: setShowRefreshTokenExpiredPopupWithBroadcast,
        handleTokenRefresh,
        ensureValidToken,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppState = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
};
